import React, { FormEvent } from 'react';
import { RouteComponentProps } from 'react-router-dom';
import { Cascader } from 'antd-mobile';
import type { CascaderOption } from 'antd-mobile';
import { getAddressRegionList, getUserAddressDetail, insertUserAddress, updateUserAddress } from '@server';
import Toast from '@components/Toast';
import { BASE_IMG_URL } from '@images/imageUrls';
export type AddressFormRouteState = { address?: any; addressId?: string | number };
export type RegionItem = { code: string | number; name: string };
type MobileCascaderValue = string | number;
type MobileCascaderValueExtend = {
    items: (CascaderOption | null)[];
    isLeaf: boolean;
};

interface AddressFormContentProps extends RouteComponentProps<{}, {}, AddressFormRouteState> {
    styles: Record<string, string>;
    mobileCascader?: boolean;
}

interface AddressFormState {
    name: string;
    phone: string;
    detailAddress: string;
    provinceId: string;
    cityId: string;
    areaId: string;
    regionText: string;
    provinces: RegionItem[];
    cities: RegionItem[];
    areas: RegionItem[];
    mobileRegionOptions: CascaderOption[];
    pickerOpen: boolean;
    regionLoading: boolean;
    addressDetailLoading: boolean;
    submitting: boolean;
    error: string;
}

// 地址表单的端无关业务逻辑，PC 和 H5 只通过样式模块呈现不同布局。
export default class AddressFormContent extends React.Component<AddressFormContentProps, AddressFormState> {
    constructor(props: AddressFormContentProps) {
        super(props);
        const address = props.location.state?.address || {};
        const relationIds = String(address.relation_ids || '').split(',');

        this.state = {
            name: String(address.true_name ?? ''),
            phone: String(address.phone ?? ''),
            detailAddress: String(address.detail_address ?? ''),
            provinceId: relationIds[0] || '',
            cityId: relationIds[1] || '',
            areaId: relationIds[2] || '',
            regionText: [address.province, address.city, address.area].filter(Boolean).join(' '),
            provinces: [],
            cities: [],
            areas: [],
            mobileRegionOptions: [],
            pickerOpen: false,
            regionLoading: false,
            addressDetailLoading: false,
            submitting: false,
            error: '',
        };
    }

    componentDidMount() {
        document.title = '填写收货地址';
        this.loadAddressDetail();
    }

    componentWillUnmount() {
        document.title = '';
    }

    // 从当前路由状态中读取编辑地址；没有地址时即为新增模式。
    getAddress = () => this.props.location.state?.address || {};

    // 读取当前编辑地址 ID，兼容路由单独传 ID 和地址对象传 ID。
    getAddressId = () => {
        const address = this.getAddress();
        return this.props.location.state?.addressId
            ?? address.id
            ?? address.addressId
            ?? address.address_id;
    };

    // 编辑地址进入页面时读取服务端详情，避免使用列表中的不完整数据。
    loadAddressDetail = async () => {
        const addressId = this.getAddressId();
        if (!addressId) return;

        this.setState({ addressDetailLoading: true, error: '' });
        try {
            const res = await getUserAddressDetail({ id: addressId });
            if (res.code !== 0) {
                this.setState({ addressDetailLoading: false, error: res.msg || '地址详情加载失败，请稍后重试' });
                return;
            }

            const detail = res.data?.address || res.data?.data || res.data || {};
            const relationIds = String(detail.relation_ids ?? detail.relationIds ?? '').split(',');
            const provinceId = detail.province_id ?? detail.provinceId ?? relationIds[0] ?? '';
            const cityId = detail.city_id ?? detail.cityId ?? relationIds[1] ?? '';
            const areaId = detail.area_id ?? detail.areaId ?? relationIds[2] ?? '';
            const regionText = [
                detail.province || detail.provinceName,
                detail.city || detail.cityName,
                detail.area || detail.district || detail.areaName || detail.districtName,
            ].filter(Boolean).join(' ');

            this.setState(prevState => ({
                name: String(detail.true_name ?? detail.trueName ?? prevState.name),
                phone: String(detail.phone ?? detail.mobile ?? prevState.phone),
                detailAddress: String(detail.detail_address ?? detail.detailAddress ?? prevState.detailAddress),
                provinceId: String(provinceId || prevState.provinceId),
                cityId: String(cityId || prevState.cityId),
                areaId: String(areaId || prevState.areaId),
                regionText: regionText || prevState.regionText,
                addressDetailLoading: false,
            }));
        } catch (_) {
            this.setState({ addressDetailLoading: false, error: '地址详情加载失败，请稍后重试' });
        }
    };

    // 请求指定层级的地区列表，并统一转换为表单需要的数组结构。
    fetchRegions = async (code?: string | number): Promise<RegionItem[]> => {
        const res = await getAddressRegionList(code ? { code } : {});
        return res.code === 0 && Array.isArray(res.data) ? res.data as RegionItem[] : [];
    };

    // 将接口地区数据转换为 Ant Design Mobile Cascader 的选项结构。
    toMobileRegionOptions = (items: RegionItem[], hasChildren: boolean): CascaderOption[] => items.map(item => ({
        value: String(item.code),
        label: item.name,
        ...(hasChildren ? { children: [{ value: `loading-${item.code}`, label: '地区加载中...', disabled: true }] } : {}),
    }));

    // 按当前级联路径查找需要补充下级地区的选项。
    findMobileRegionOption = (values: string[]) => {
        let options = this.state.mobileRegionOptions;
        let target: CascaderOption | undefined;
        values.forEach(value => {
            target = options.find(item => String(item.value) === String(value));
            options = target?.children || [];
        });
        return target;
    };

    // H5 首次打开级联选择器时加载省份选项。
    loadMobileProvinces = async () => {
        if (this.state.mobileRegionOptions.length || this.state.regionLoading) return;
        this.setState({ regionLoading: true, error: '' });
        try {
            const provinceList = await this.fetchRegions();
            this.setState({ mobileRegionOptions: this.toMobileRegionOptions(provinceList, true), regionLoading: false });
        } catch (_) {
            this.setState({ error: '地区数据加载失败，请稍后重试', regionLoading: false });
        }
    };

    // H5 选择省或市后按需加载对应的城市或区县列表。
    loadMobileChildren = async (value: MobileCascaderValue[]) => {
        const values = value.map(item => String(item));
        if (values.length < 1 || values.length > 2) return;
        const target = this.findMobileRegionOption(values);
        if (!target || !target.children || !target.children.some(item => String(item.value).startsWith('loading-'))) return;

        this.setState({ regionLoading: true, error: '' });
        try {
            const children = await this.fetchRegions(target.value as string);
            target.children = this.toMobileRegionOptions(children, values.length === 1);
            this.setState(prevState => ({ mobileRegionOptions: [...prevState.mobileRegionOptions], regionLoading: false }));
        } catch (_) {
            this.setState({ error: '地区数据加载失败，请稍后重试', regionLoading: false });
        }
    };

    // H5 级联选择发生变化时，保存暂选路径并触发下一级地区加载。
    handleMobileRegionSelect = (value: MobileCascaderValue[]) => {
        this.loadMobileChildren(value);
    };

    // H5 点击确认后写入完整的省、市、区编码和名称。
    handleMobileRegionConfirm = (value: MobileCascaderValue[], extend: MobileCascaderValueExtend) => {
        if (!extend.isLeaf || value.length < 3) return;
        const selectedItems = extend.items.filter(Boolean) as CascaderOption[];
        this.setState({
            provinceId: String(value[0]),
            cityId: String(value[1]),
            areaId: String(value[2]),
            regionText: selectedItems.map(item => item.label).join(' '),
            pickerOpen: false,
        });
    };

    // 提取当前已确认的省、市、区编码，作为 Cascader 的受控值。
    getRegionValue = () => [this.state.provinceId, this.state.cityId, this.state.areaId].filter(Boolean);

    // 渲染 Ant Design Mobile 的 H5 地区级联选择器和底部弹层触发区域。
    renderMobileRegion = () => {
        const { styles } = this.props;
        const { mobileRegionOptions, pickerOpen, regionLoading, regionText } = this.state;
        return <Cascader
            className={styles.mobileCascader}
            options={mobileRegionOptions}
            value={this.getRegionValue()}
            visible={pickerOpen}
            title=""
            confirmText="确认"
            cancelText="取消"
            loading={regionLoading}
            onSelect={this.handleMobileRegionSelect}
            onConfirm={this.handleMobileRegionConfirm}
            onCancel={() => this.setState({ pickerOpen: false })}
            onClose={() => this.setState({ pickerOpen: false })}
        >
            {(_, actions) => <button
                type="button"
                className={styles.regionRow}
                onClick={() => {
                    this.setState({ pickerOpen: true });
                    this.loadMobileProvinces();
                    actions.open();
                }}
            >
                <span className={styles.fieldLabel}>所在地区</span>
                <b className={`${styles.regionValue} ${regionText ? '' : styles.placeholder}`}>{regionText || '请选择省/市/县/（区）'}</b>
                <img src={`${BASE_IMG_URL}/goods/jtr1.png`} alt="" className={styles.regionArrow} />
            </button>}
        </Cascader>;
    };

    // 打开地区选择器，并在首次打开时初始化省、市、区数据。
    openPicker = async () => {
        this.setState({ pickerOpen: true });
        if (this.state.provinces.length) return;

        this.setState({ regionLoading: true, error: '' });
        try {
            const provinceList = await this.fetchRegions();
            const selectedProvince = String(this.state.provinceId || provinceList[0]?.code || '');
            const cityList = selectedProvince ? await this.fetchRegions(selectedProvince) : [];
            const selectedCity = String(this.state.cityId || cityList[0]?.code || '');
            const areaList = selectedCity ? await this.fetchRegions(selectedCity) : [];

            this.setState({
                provinces: provinceList,
                cities: cityList,
                areas: areaList,
                provinceId: selectedProvince,
                cityId: selectedCity,
                areaId: String(this.state.areaId || areaList[0]?.code || ''),
                regionLoading: false,
            });
        } catch (_) {
            this.setState({ error: '地区数据加载失败，请稍后重试', regionLoading: false });
        }
    };

    // 切换省份时重新加载城市和下级地区，并重置下级选项。
    changeProvince = async (code: string) => {
        this.setState({ provinceId: code, regionLoading: true, error: '' });
        try {
            const cityList = await this.fetchRegions(code);
            const nextCity = String(cityList[0]?.code || '');
            const areaList = nextCity ? await this.fetchRegions(nextCity) : [];
            this.setState({
                cities: cityList,
                areas: areaList,
                cityId: nextCity,
                areaId: String(areaList[0]?.code || ''),
                regionLoading: false,
            });
        } catch (_) {
            this.setState({ error: '地区数据加载失败，请稍后重试', regionLoading: false });
        }
    };

    // 切换城市时重新加载区县，并将区县默认定位到第一项。
    changeCity = async (code: string) => {
        this.setState({ cityId: code, regionLoading: true, error: '' });
        try {
            const areaList = await this.fetchRegions(code);
            this.setState({ areas: areaList, areaId: String(areaList[0]?.code || ''), regionLoading: false });
        } catch (_) {
            this.setState({ error: '地区数据加载失败，请稍后重试', regionLoading: false });
        }
    };

    // 确认地区选择并将名称组合成表单展示文本。
    confirmRegion = () => {
        const { provinces, cities, areas, provinceId, cityId, areaId } = this.state;
        const province = provinces.find(item => String(item.code) === String(provinceId));
        const city = cities.find(item => String(item.code) === String(cityId));
        const area = areas.find(item => String(item.code) === String(areaId));
        if (!province || !city || !area) return;

        this.setState({ regionText: `${province.name} ${city.name} ${area.name}`, pickerOpen: false });
    };

    // 校验必填字段，控制提交按钮是否可用。
    canSubmit = () => {
        const { name, phone, provinceId, cityId, areaId, regionText, detailAddress } = this.state;
        return Boolean(
            name.trim()
            && /^1\d{10}$/.test(phone.trim())
            && provinceId
            && cityId
            && areaId
            && regionText.trim()
            && detailAddress.trim()
        );
    };

    // 新增或更新收货地址，成功后返回地址列表页。
    submit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (!this.canSubmit() || this.state.submitting) return;

        const addressId = this.getAddressId();
        const params = {
            trueName: this.state.name.trim(),
            phone: this.state.phone.trim(),
            provinceId: this.state.provinceId,
            cityId: this.state.cityId,
            areaId: this.state.areaId,
            detailAddress: this.state.detailAddress.trim(),
            ...(addressId ? { id: addressId } : {}),
        };

        this.setState({ submitting: true, error: '' });
        try {
            const isEditing = Boolean(addressId);
            const res = isEditing ? await updateUserAddress(params) : await insertUserAddress(params);
            if (res.code === 0) {
                this.props.history.goBack();
                return;
            }
            const message = res.msg || `${isEditing ? '修改' : '新增'}地址失败，请稍后重试`;
            if (isEditing) {
                // 修改地址失败优先展示接口提示，没有提示时使用默认文案。
                Toast.error({ content: message });
                this.setState({ error: '' });
            } else {
                this.setState({ error: message });
            }
        } catch (_) {
            const message = `${addressId ? '修改' : '新增'}地址失败，请稍后重试`;
            if (addressId) {
                // 网络异常时使用修改地址的默认失败提示。
                Toast.error({ content: message });
                this.setState({ error: '' });
            } else {
                this.setState({ error: message });
            }
        } finally {
            this.setState({ submitting: false });
        }
    };

    // 渲染省、市、区三级选择器，选择器外观由当前端样式控制。
    renderPicker = () => {
        const { styles } = this.props;
        const { pickerOpen, regionLoading, provinces, cities, areas, provinceId, cityId, areaId } = this.state;
        if (!pickerOpen) return null;

        return <div className={styles.pickerLayer}>
            {/* 遮罩层：点击空白区域关闭地区选择器 */}
            <button type="button" className={styles.mask} onClick={() => this.setState({ pickerOpen: false })} aria-label="关闭地区选择" />
            {/* 地区选择面板：包含取消、确认及三级联动选择 */}
            <section className={styles.pickerPanel}>
                <header className={styles.pickerHeader}>
                    <button className={styles.pickerCancel} type="button" onClick={() => this.setState({ pickerOpen: false })}>取消</button>
                    <h2 className={styles.pickerTitle}>选择所在地区</h2>
                    <button className={styles.pickerConfirm} type="button" onClick={this.confirmRegion} disabled={regionLoading}>确认</button>
                </header>
                {regionLoading && !provinces.length
                    ? <div className={styles.pickerState}>地区加载中...</div>
                    : <div className={styles.regionColumns}>
                        <select className={styles.regionSelect} value={provinceId} onChange={event => this.changeProvince(event.target.value)}>
                            {provinces.map(item => <option key={item.code} value={item.code}>{item.name}</option>)}
                        </select>
                        <select className={styles.regionSelect} value={cityId} onChange={event => this.changeCity(event.target.value)} disabled={regionLoading}>
                            {cities.map(item => <option key={item.code} value={item.code}>{item.name}</option>)}
                        </select>
                        <select className={styles.regionSelect} value={areaId} onChange={event => this.setState({ areaId: event.target.value })} disabled={regionLoading}>
                            {areas.map(item => <option key={item.code} value={item.code}>{item.name}</option>)}
                        </select>
                    </div>}
            </section>
        </div>;
    };

    render() {
        const { styles } = this.props;
        const { name, phone, detailAddress, regionText, error, submitting, addressDetailLoading } = this.state;
        const { mobileCascader } = this.props;
        const canSubmit = this.canSubmit();

        return <div className={styles.page}>
            <form onSubmit={this.submit}>
                {/* 收货地址基础信息：姓名、手机号、地区和详细地址 */}
                <div className={styles.formCard}>
                    <label className={styles.fieldRow}>
                        <span className={styles.fieldLabel}>收货人</span>
                        <input className={styles.fieldInput} value={name} onChange={event => this.setState({ name: event.target.value })} placeholder="请填写收货人姓名" maxLength={30} />
                    </label>
                    <label className={styles.fieldRow}>
                        <span className={styles.fieldLabel}>手机号</span>
                        <input className={styles.fieldInput} type="tel" inputMode="numeric" value={phone} onChange={event => this.setState({ phone: event.target.value.replace(/\D/g, '').slice(0, 11) })} placeholder="请填写收货人手机号" />
                    </label>
                    {mobileCascader ? this.renderMobileRegion() : <button type="button" className={styles.regionRow} onClick={this.openPicker}>
                        <span className={styles.fieldLabel}>所在地区</span>
                        <b className={`${styles.regionValue} ${regionText ? '' : styles.placeholder}`}>{regionText || '点击选择省/市/地区'}</b>
                        <img src={`${BASE_IMG_URL}/goods/jtr1.png`} alt="" className={styles.regionArrow} />
                    </button>}
                    <label className={styles.fieldRow}>
                        <span className={styles.fieldLabel}>详细地址</span>
                        <input className={styles.fieldInput} value={detailAddress} onChange={event => this.setState({ detailAddress: event.target.value })} placeholder="如街道、门牌号、小区、乡镇等" maxLength={100} />
                    </label>
                </div>
                {/* 提交错误提示和保存按钮 */}
                {error && <p className={styles.error}>{error}</p>}
                <div className={styles.submitBar}>
                    <button className={styles.submitButton} type="submit" disabled={!canSubmit || submitting || addressDetailLoading}>
                        {addressDetailLoading ? '加载中...' : submitting ? '提交中...' : '提交'}
                    </button>
                </div>
            </form>
            {/* H5 使用 antd-mobile Cascader；仅桌面版旧布局渲染自定义三级选择器。 */}
            {!mobileCascader && this.renderPicker()}
        </div>;
    }
}
